import React, { useEffect, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate, useMatch } from 'react-router-dom'
import {
  Plus,
  Filter as FilterIcon,
  RefreshCw,
  Save,
  Clipboard,
  ClipboardList,
  Icon,
  MapPin,
} from 'lucide-react'

import {
  OrderActionsProvider,
  useOrderActions,
} from '../../../contexts/OrderActionsContext'

import FilterMenu from '../../../components/SubHeaderFilter/FilterMenu'
import AppliedFiltersDisplay from '../../../components/SubHeaderFilter/AppliedFiltersDisplay'
import { Filter } from '../../../components/SubHeaderFilter/types'


import { steelOrdersTableHeaders } from './SteelOrdersTable/utils'
import ConfirmationModal from '../../../components/ModalConfirmation'
import {
  OptionsButton,
  LeftSide,
  RightSide,
  SubHeaderContainer,
  RefreshIconContainer,
  FilterWrapper,
  Tab,
  TabsDiv,
} from '../../../components/styles/subheader-style'

import {
  Content,
  SectionName,
  TabsContainer,
  TabButton,
  TabPlaceholder,
} from './style'
import { Button } from './AddOrder/style'
import jwtDecode from 'jwt-decode'

const TAB_INDEX_BUDGET = 0
const TAB_INDEX_TRACKING = 1

const steelTabs = [
  {
    path: '/panel/steel',
    name: 'Orçamentos',
    index: TAB_INDEX_BUDGET,
  },
  {
    path: '/panel/steel/tracking',
    name: 'Rastreio',
    index: TAB_INDEX_TRACKING,
  },
]

const SteelOrdersLayout: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const trackingMatch = useMatch('/panel/steel/tracking')
  const { triggerSaveOrder } = useOrderActions()

  const [refresh, setRefresh] = useState(false)
  const [appliedFilters, setAppliedFilters] = useState<Filter[]>([])
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const filterButtonRef = useRef<HTMLDivElement | null>(null)

  const [activeTab, setActiveTab] = useState<'orcamentos' | 'rastreio'>(
    'orcamentos',
  )

  const isCreatePage = location.pathname.includes('/panel/steel/create')
  const isEditPage = location.pathname.includes('/panel/steel/edit')
  const tabIndex = trackingMatch ? TAB_INDEX_TRACKING : TAB_INDEX_BUDGET
  const isFormPage = isCreatePage || isEditPage
  const newOrderId = location.state?.newOrderId

  const token = 
  document.cookie.split('; ').find(row => row.startsWith('token='))?.split('=')[1] || 
  localStorage.getItem('token') || 
  localStorage.getItem('token'); 

  const handleApplyFilter = (newFilter: Filter) => {
    setAppliedFilters((prev) => [...prev, newFilter])
  }
  const handleRemoveFilter = (index: number) => {
    setAppliedFilters((prev) => prev.filter((_, i) => i !== index))
  }
  const handleClearAll = () => {
    setAppliedFilters([])
  }
  const handleCancel = () => {
    setShowCancelModal(false)
    navigate('/panel/steel')
  }

  let userRole = '';
  if (token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    const decoded = JSON.parse(jsonPayload);
    userRole = decoded.role;
  } catch (e) {
    console.error("❌ Erro ao decodificar token no SubHeader", e);
  }
} else {
  console.warn("⚠️ Token não encontrado no SubHeader!");
}

  const isTenantClient = userRole === 'TENANT_CLIENT';

  useEffect(() => {
  if (isTenantClient && location.pathname === '/panel/steel') {
    navigate('/panel/steel/tracking', { replace: true });
  }
}, [isTenantClient, location.pathname, navigate]);

  return (
    <>
      <SubHeaderContainer>
        <LeftSide>
          <SectionName>
            {isFormPage
              ? isEditPage
                ? 'Editar Pedido'
                : `Novo Pedido: ${newOrderId || ''}`
              : 'Vendas aço'}
          </SectionName>

          {!isFormPage && (
            <TabsDiv>
              {steelTabs
              .filter(tab => !(isTenantClient && tab.index === TAB_INDEX_BUDGET))
              .map((tab, index) => (
                <Tab
                  key={tab.index}
                  $isFirst={tab.index === 0}
                  $isLast={tab.index === steelTabs.length - 1}
                  $isActive={tab.index === tabIndex}
                  onClick={() => navigate(tab.path)}
                >
                  {index === 0 ? (
                    <ClipboardList size={12.9} style={{ marginRight: 7 }} />
                  ) : (
                    <MapPin size={12.8} style={{ marginRight: 7 }} />
                  )}

                  {tab.name}
                </Tab>
              ))}
            </TabsDiv>
          )}
        </LeftSide>

        <RightSide>
          {!isFormPage && (
            <>
              {!isTenantClient && (
                <>
              <RefreshIconContainer
                onClick={() => setRefresh((prev) => !prev)}
                title='Atualizar'
              >
                <RefreshCw size={20} />
              </RefreshIconContainer>
              <FilterWrapper>
                <OptionsButton
                  ref={filterButtonRef}
                  onClick={() => setIsFilterMenuOpen(true)}
                >
                  <FilterIcon size={10} />
                </OptionsButton>
                {isFilterMenuOpen && (
                  <FilterMenu
                    headers={steelOrdersTableHeaders}
                    onApplyFilter={handleApplyFilter}
                    onClose={() => setIsFilterMenuOpen(false)}
                    anchorRef={filterButtonRef}
                    filters={appliedFilters}
                  />
                )}
              </FilterWrapper>
            </>
              )}
            </>
          )}

          {isFormPage ? (
            <>
              <Button
                variant='danger-outline'
                onClick={() => setShowCancelModal(true)}
              >
                Cancelar
              </Button>
              <Button variant='primary' onClick={triggerSaveOrder}>
                <Save size={18} style={{ marginRight: '8px' }} />
                {isEditPage ? 'Salvar Alterações' : 'Salvar Pedido'}
              </Button>
            </>
          ) : (
            !isTenantClient && (
              <Button
              variant='primary'
              onClick={() => navigate('/panel/steel/create')}
            >
              <Plus size={18} style={{ marginRight: '8px' }} />
              Novo Pedido
            </Button>
            )
          )}
        </RightSide>
      </SubHeaderContainer>

      <AppliedFiltersDisplay
        filters={appliedFilters}
        onRemoveFilter={handleRemoveFilter}
        onClearAll={handleClearAll}
      />

      <Content>
        <Outlet context={{ refresh, appliedFilters }} />
      </Content>

      {showCancelModal && (
        <ConfirmationModal
          isOpen={showCancelModal}
          title={
            isEditPage
              ? 'Cancelar Edição de Pedido'
              : 'Cancelar Criação de Pedido'
          }
          message='Você tem certeza? Todas as alterações não salvas serão perdidas.'
          actions={[
            { label: 'Confirmar', onClick: handleCancel, variant: 'danger' },
            {
              label: 'Voltar',
              onClick: () => setShowCancelModal(false),
              variant: 'outline',
            },
          ]}
        />
      )}
    </>
  )
}

const SteelOrders: React.FC = () => {
  return (
    <OrderActionsProvider>
      <SteelOrdersLayout />
    </OrderActionsProvider>
  )
}

export default SteelOrders
